import { useEffect, useMemo } from 'react';
import type { PropsWithChildren } from 'react';
import { getObservabilityClient } from '@locus-taxy/encore-observability/observability';
import { useAuth0 } from '../auth';
import { AUTH0_CUSTOM_CLAIMS_NAMESPACE } from '../config';
import { getBugsnagClient } from '../utils/bugsnag';
import {
  UserContext,
  type UserInfo,
  type UserContextValue,
} from './UserContextDefinition';

export function UserProvider({ children }: Readonly<PropsWithChildren>) {
  const { user, isLoading, isAuthenticated } = useAuth0();

  const userInfo = useMemo<UserInfo | null>(() => {
    if (!isAuthenticated || !user) {
      return null;
    }

    // Extract locusUsername from custom claims in ID token
    const customClaims = user[AUTH0_CUSTOM_CLAIMS_NAMESPACE] as
      | Record<string, unknown>
      | undefined;
    const locusUsername = customClaims?.locusUsername as string | undefined;

    // Use locusUsername as the primary userId, with fallbacks (empty string treated as absent)
    const effectiveLocusUsername = locusUsername?.trim()
      ? locusUsername
      : undefined;
    const fallbackUserId = user.sub ?? user.user_id ?? user.email ?? 'unknown';
    const userId = effectiveLocusUsername ?? fallbackUserId;

    return {
      userId,
      email: user.email,
      name: user.name,
      picture: user.picture,
      // Include custom claims for additional context
      clientId: customClaims?.clientId as string | undefined,
      homeTenant: customClaims?.homeTenant as string | undefined,
      roleId: customClaims?.roleId as string | undefined,
      targetTenant: customClaims?.targetTenant as string | undefined,
      ...user, // Include any additional user properties
    };
  }, [user, isAuthenticated]);

  const contextValue = useMemo<UserContextValue>(
    () => ({
      user: userInfo,
      isLoading,
    }),
    [userInfo, isLoading]
  );

  useEffect(() => {
    if (!userInfo?.userId) {
      return;
    }

    const observabilityClient = getObservabilityClient();
    const userId = userInfo.userId;

    let uniqueUserIdentifier: string;

    if (userId === userInfo.email) {
      // If userId is email, then split it and use the first part as the userId
      // This is to avoid passing raw email to o11y vendor as it may contain PII
      uniqueUserIdentifier = `${userId.split('@')[0]}/${userInfo.clientId}`;
    } else {
      uniqueUserIdentifier = `${userId}/${userInfo.clientId}`;
    }

    if (observabilityClient) {
      observabilityClient.identifyUser({
        // Avoid passing raw email to o11y vendor as it may contain PII
        // Groundcover dashboard picks email key as user identifier in UI table
        email: uniqueUserIdentifier,
        id: uniqueUserIdentifier,
      });
    }

    const bugsnagClient = getBugsnagClient();
    if (bugsnagClient) {
      bugsnagClient.setUser(
        uniqueUserIdentifier,
        undefined,
        userInfo.name ?? undefined
      );
    }
  }, [userInfo]);

  return (
    <UserContext.Provider value={contextValue}>{children}</UserContext.Provider>
  );
}
